/**
 * "Keywords in AI notes…" — the AI-notes search (Trello #779), beside
 * `NameSearchBar` in the filter row. Rendered only on AI-reviewed lists: the
 * search reads the rationales of the list's latest applied AI review, so
 * anywhere else it could only ever match nobody.
 *
 * Fully controlled and presentational, like its siblings: the state, the
 * 300 ms debounce and the page-1 reset live in `ImportDetailPage`. The count
 * line is the page's to compute too (`matchCount`), because only the page
 * knows whether the rows on screen belong to the terms in the box yet.
 */
import { InfoHint } from "./InfoHint";
import { filterInputClass } from "./filterInputClass";

/** The sparkle the options artifact drew in this box: marks it as a search
 *  over what the AI wrote, not over the person's own fields. */
function AiSparkle() {
  return (
    <svg
      data-testid="contacts-notes-search-ai-icon"
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinejoin="round"
      className="pointer-events-none absolute inset-y-0 left-2.5 my-auto h-4 w-4 text-accent"
    >
      <path d="M12 3l1.8 4.6L18 9.4l-4.2 1.8L12 16l-1.8-4.8L6 9.4l4.2-1.8z" />
    </svg>
  );
}

export function NotesSearchBar({
  notesQuery,
  onNotesQueryChange,
  matchCount,
}: {
  notesQuery: string;
  onNotesQueryChange: (value: string) => void;
  /** "N of M mention … in AI notes"; `null` hides the line. `of` is `null`
   *  while other filters narrow the list too: M is the whole list, so "of M"
   *  would credit the notes with what the other filters removed. */
  matchCount?: { count: number; of: number | null; terms: readonly string[] } | null;
}) {
  return (
    <fieldset
      data-testid="notes-search-filter"
      className="flex flex-row flex-wrap items-center gap-3"
    >
      <legend className="sr-only">Search the AI review notes</legend>
      <div className="relative">
        <AiSparkle />
        <input
          type="text"
          data-testid="contacts-notes-search"
          aria-label="Search the AI review notes by keyword"
          placeholder="Keywords in AI notes…"
          value={notesQuery}
          onChange={(e) => onNotesQueryChange(e.target.value)}
          className={`w-80 pl-8 pr-8 ${filterInputClass}`}
        />
        {notesQuery.length > 0 ? (
          <button
            type="button"
            data-testid="contacts-notes-search-clear"
            aria-label="Clear the AI notes search"
            onClick={() => onNotesQueryChange("")}
            className="absolute inset-y-0 right-2 inline-flex items-center text-text-muted hover:text-text"
          >
            ×
          </button>
        ) : null}
      </div>
      <InfoHint
        centered
        label="How the AI notes search works"
        content={
          <>
            Words match the start of a word in the AI notes (<b>pay</b> finds “payments”). Several
            words find people whose notes mention any of them. Put words in <b>"quotes"</b> to find
            an exact phrase, e.g. <b>"private equity"</b>.
          </>
        }
      />
      {/* The live region stays mounted: one inserted together with its text
          is often not announced. Empty, it is `sr-only` (absolutely placed),
          so it takes no slot — and no gap — in the filter row. */}
      <span
        data-testid="notes-match-live"
        aria-live="polite"
        className="text-caption text-text-secondary empty:sr-only"
      >
        {matchCount && matchCount.terms.length > 0 ? (
          <span data-testid="notes-match-count">
            {matchCount.of === null
              ? `${matchCount.count} of the filtered people`
              : `${matchCount.count} of ${matchCount.of}`}{" "}
            mention {matchCount.terms.map((t) => `“${t}”`).join(" or ")} in AI notes
          </span>
        ) : null}
      </span>
    </fieldset>
  );
}
